import React, { useEffect, useState, useCallback } from 'react';
import { Plus, RefreshCw, Edit3, Trash2 } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = 'https://baustellenmgmt.mas123.de/api';

const DEFAULT_SUBUNTERNEHMER = {
  firma: '',
  ansprechpartner: '',
  email: '',
  telefon: '',
  stundensatz: '',
  ist_aktiv: true,
  notizen: ''
};

function SubunternehmerView({ token }) {
  const [subunternehmer, setSubunternehmer] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editingSubunternehmer, setEditingSubunternehmer] = useState(null);
  const [newSubunternehmer, setNewSubunternehmer] = useState(null);

  const loadSubunternehmer = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/core/subunternehmer/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSubunternehmer(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Subunternehmer:', error);
      alert('Fehler beim Laden der Subunternehmer');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    loadSubunternehmer();
  }, [loadSubunternehmer]);

  function normalizeSubunternehmer(data) {
    return {
      id: data.id,
      firma: data.firma || '',
      ansprechpartner: data.ansprechpartner || '',
      email: data.email || '',
      telefon: data.telefon || '',
      stundensatz: data.stundensatz ?? '',
      ist_aktiv: data.ist_aktiv ?? true,
      notizen: data.notizen || ''
    };
  }

  async function startEditSubunternehmer(entry) {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/subunternehmer/${entry.id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEditingSubunternehmer(normalizeSubunternehmer(response.data));
    } catch (error) {
      console.error('Fehler beim Laden des Subunternehmers:', error);
      setEditingSubunternehmer(normalizeSubunternehmer(entry));
    }
  }

  async function saveSubunternehmer(entry) {
    const payload = {
      firma: entry.firma,
      ansprechpartner: entry.ansprechpartner,
      email: entry.email,
      telefon: entry.telefon,
      stundensatz: entry.stundensatz === '' ? null : Number(entry.stundensatz),
      ist_aktiv: Boolean(entry.ist_aktiv),
      notizen: entry.notizen
    };

    try {
      if (entry.id) {
        await axios.put(`${API_BASE_URL}/core/subunternehmer/${entry.id}/`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_BASE_URL}/core/subunternehmer/`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      setEditingSubunternehmer(null);
      setNewSubunternehmer(null);
      loadSubunternehmer();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      const errorMsg = error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Speichern';
      alert(errorMsg);
    }
  }

  async function deleteSubunternehmer(id) {
    if (!window.confirm('Subunternehmer wirklich loeschen?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/core/subunternehmer/${id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadSubunternehmer();
    } catch (error) {
      console.error('Fehler beim Loeschen:', error);
      alert('Fehler beim Loeschen des Subunternehmers');
    }
  }

  const filteredSubunternehmer = subunternehmer.filter((entry) => {
    const needle = search.trim().toLowerCase();
    const matchSearch =
      !needle ||
      (entry.firma || '').toLowerCase().includes(needle) ||
      (entry.ansprechpartner || '').toLowerCase().includes(needle) ||
      (entry.email || '').toLowerCase().includes(needle) ||
      (entry.telefon || '').toLowerCase().includes(needle);
    const matchActive = showInactive || entry.ist_aktiv;
    return matchSearch && matchActive;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>Subunternehmer</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={loadSubunternehmer} className="btn" disabled={loading}>
            <RefreshCw size={18} className={loading ? 'spinning' : ''} /> Aktualisieren
          </button>
          {!newSubunternehmer && !editingSubunternehmer && (
            <button
              onClick={() => setNewSubunternehmer({ ...DEFAULT_SUBUNTERNEHMER })}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Plus size={18} /> Neuer Subunternehmer
            </button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', alignItems: 'center' }}>
          <input
            type="text"
            className="input"
            placeholder="Suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.875rem', color: '#374151' }}>Nicht Aktive auch anzeigen</span>
          </label>
        </div>
      </div>

      {(newSubunternehmer || editingSubunternehmer) && (
        <SubunternehmerForm
          subunternehmer={editingSubunternehmer || newSubunternehmer}
          isEditing={Boolean(editingSubunternehmer)}
          onChange={(entry) => editingSubunternehmer ? setEditingSubunternehmer(entry) : setNewSubunternehmer(entry)}
          onSave={() => saveSubunternehmer(editingSubunternehmer || newSubunternehmer)}
          onCancel={() => {
            setEditingSubunternehmer(null);
            setNewSubunternehmer(null);
          }}
        />
      )}

      <div className="card">
        {loading && subunternehmer.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            Lade Subunternehmer...
          </div>
        ) : filteredSubunternehmer.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            Keine Subunternehmer gefunden
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Firma</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Ansprechpartner</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>E-Mail</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Telefon</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Stundensatz</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Status</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredSubunternehmer.map((entry) => (
                  <tr key={entry.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem 0.5rem', fontWeight: 600 }}>
                      {entry.firma}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>{entry.ansprechpartner || '-'}</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>{entry.email || '-'}</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>{entry.telefon || '-'}</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      {entry.stundensatz != null ? `${entry.stundensatz} EUR` : '-'}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <span
                        style={{
                          padding: '0.25rem 0.6rem',
                          borderRadius: '999px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          background: entry.ist_aktiv ? '#dcfce7' : '#fee2e2',
                          color: entry.ist_aktiv ? '#166534' : '#991b1b'
                        }}
                      >
                        {entry.ist_aktiv ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn"
                          onClick={() => startEditSubunternehmer(entry)}
                          title="Bearbeiten"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          className="btn"
                          style={{ color: '#dc2626' }}
                          onClick={() => deleteSubunternehmer(entry.id)}
                          title="Loeschen"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SubunternehmerForm({ subunternehmer, isEditing, onChange, onSave, onCancel }) {
  return (
    <div className="card" style={{ marginBottom: '1.5rem', background: '#f9fafb' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
          {isEditing ? 'Subunternehmer bearbeiten' : 'Neuer Subunternehmer'}
        </h3>
      </div>

      <div style={{ display: 'grid', gap: '1rem' }}>
        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Firma *
            </label>
            <input
              type="text"
              value={subunternehmer.firma}
              onChange={(e) => onChange({ ...subunternehmer, firma: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Ansprechpartner *
            </label>
            <input
              type="text"
              value={subunternehmer.ansprechpartner}
              onChange={(e) => onChange({ ...subunternehmer, ansprechpartner: e.target.value })}
              className="input"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              E-Mail
            </label>
            <input
              type="email"
              value={subunternehmer.email}
              onChange={(e) => onChange({ ...subunternehmer, email: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Telefon
            </label>
            <input
              type="text"
              value={subunternehmer.telefon}
              onChange={(e) => onChange({ ...subunternehmer, telefon: e.target.value })}
              className="input"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Stundensatz *
            </label>
            <input
              type="number"
              step="0.01"
              value={subunternehmer.stundensatz}
              onChange={(e) => onChange({ ...subunternehmer, stundensatz: e.target.value })}
              className="input"
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
            <input
              type="checkbox"
              checked={subunternehmer.ist_aktiv}
              onChange={(e) => onChange({ ...subunternehmer, ist_aktiv: e.target.checked })}
              style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.875rem', color: '#374151' }}>Aktiv</span>
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Notizen
          </label>
          <textarea
            value={subunternehmer.notizen}
            onChange={(e) => onChange({ ...subunternehmer, notizen: e.target.value })}
            className="input"
            rows={4}
          />
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} className="btn">Abbrechen</button>
          <button onClick={onSave} className="btn btn-primary">Speichern</button>
        </div>
      </div>
    </div>
  );
}

export default SubunternehmerView;
